<template>
  <div
    v-if="!isLoading"
    class="meeting-container"
  >
    <!-- VISIO CONTAINER -->
    <div class="visio-container">
      <!-- PARTICIPANTS -->
      <div class="participants-container">
        <div class="participant-pin">
          <Participant
            v-if="pinedParticipant !== null"
            :inspection="inspection"
            :participant="pinedParticipant"
            ref="pinedparticipant"
            @pined="pinUser"
          />
        </div>
        <div class="participant-list">
          <Participant
            v-for="p in participants"
            :inspection="inspection"
            :key="p.sid"
            :participant="p"
            :ref="`participants-${p.sid}`"
            :small="true"
            @pined="pinUser"
          />
        </div>
      </div>
      <!-- END PARTICIPANTS -->

      <!-- SIDEBAR (TCHAT/USERS/TOOLS)-->
      <SideBar
        v-model="selectedMenu"
        v-show="selectedMenu !== null"
        :inspection="inspection"
      />
      <!-- END SIDEBAR -->
    </div>
    <!-- END VISIO CONTAINER -->

    <!-- TOOLBAR -->
    <Toolbar
      :name="inspection.name"
      :selected-menu="selectedMenu"
      :visio-room="visioRoom"
      @select="updateSelectedMenu"
    />
  </div>
  <Loading v-else></Loading>
</template>

<script>
import { Client as TwChatClient } from "@twilio/conversations"
import TwVideo from "twilio-video"

import MeetingMixins from "@/mixins/meeting.mixins"
import { MeetingService } from "@/services"

import Loading from "./core/Loading.vue"
import Participant from "./core/Participant.vue"
import Toolbar from "./core/Toolbar.vue"
import SideBar from "./side-bar/SideBar.vue"

export default {
  name: "Visio",
  mixins: [MeetingMixins],
  props: {
    inspection: {
      type: Object,
      required: true
    },
    guestUsername: {
      type: String,
      default: ""
    },
    audioDeviceId: {
      type: String,
      default: undefined
    },
    videoDeviceId: {
      type: String,
      default: undefined
    }
  },
  components: {
    Loading,
    Participant,
    Toolbar,
    SideBar
  },
  /**
   * VUE - DATA/COMPUTED VALUES
   */
  computed: {
    pinedParticipant() {
      return this.visioUsers.find(e => e.isPined) || null
    },
    participants() {
      return this.visioUsers.filter(e => !e.isPined)
    }
  },
  data() {
    return {
      isLoading: true,
      selectedMenu: null
    }
  },
  /**
   * VUE - HOOKS
   */
  beforeDestroy() {
    this.$store.commit("meeting/leave")
  },
  created() {
    this.connect()
  },
  /**
   * VUE - METHODS
   */
  methods: {
    updateSelectedMenu(newValue) {
      this.selectedMenu = newValue
    },
    async pinUser(participant) {
      const updatedParticipants = []
      this.visioUsers.forEach(e => {
        if (e.sid === participant.sid) {
          // Update the pinned participant, toggle it's pin value
          e.isPined = !e.isPined
          updatedParticipants.push(e)
        } else if (e.isPined !== false) {
          // Set pin to false to all previously pinned participants
          e.isPined = false
          updatedParticipants.push(e)
        }
      })

      // Wait elements be rendered before attach tracks to DOM
      await this.$nextTick()

      // Update track attaches
      updatedParticipants.forEach(this.updateParticipantTracks)
    },
    leave() {
      this.$emit("leave-inspection")
    },
    notify(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    // TWILIO METHODS
    async authenticate() {
      if (this.$route.params.uuid) {
        return MeetingService.authorize_by_uuid(this, this.$route.params.uuid, this.guestUsername)
      }
      return Promise.resolve({})
    },
    async connect() {
      this.authenticate()
        .then(async data => {
          const p1 = this.connectChat(data.token, data.conversation_sid)
          const p2 = this.connectVisio(data.token)
          return Promise.all([p1, p2])
        })
        .then(() => {
          console.log("Connection success")
        })
        .catch(error => {
          this.notify(error, "error")
          this.leave()
        })
    },
    async connectVisio(token) {
      const options = {
        name: this.inspection.uuid,
        audio: { deviceId: this.audioDeviceId },
        video: { deviceId: this.videoDeviceId }
      }
      return TwVideo.connect(token, options).then(room => {
        // reset stored variables
        this.visioRoom = room
        this.visioUsers = []

        // Initialize user/participant list
        this.addUser(room.localParticipant, true)
        room.participants.forEach(this.addUser)

        // Add event listners on localParticipant
        const localParticipant = room.localParticipant
        localParticipant.on("trackPublished", this.onLocalTrackPublished)

        // Add events listners on room
        room.on("disconnected", this.onDisconnected)
        room.on("participantConnected", this.onParticipantConnected)
        room.on("participantDisconnected", this.onParticipantDisconnected)
        room.on("recordingStart", this.onRecordingStart)
        room.on("recordingStopped", this.onRecordingStopped)

        // No need to listen publication use subscriptions
        // https://www.twilio.com/docs/video/api/track-subscriptions
        room.on("trackSubscribed", this.onTrackSubscribed)
        room.on("trackUnsubscribed", this.onTrackUnsubscribed)

        this.isLoading = false
      })
    },
    async connectChat(token, sid) {
      return TwChatClient.create(token).then(async client => {
        this.chatClient = client
        return client.getConversationBySid(sid).then(conversation => {
          // reset store variables
          this.chatConversation = conversation
          this.chatMessages = []

          // Initialize users list
          conversation.getParticipants().then(participants => {
            this.chatUsers = participants
          })

          // Initialize messages list
          conversation.getMessages().then(messages => {
            this.chatMessages = messages.items
          })

          // Add events listners on room
          conversation.on("messageAdded", this.onChatMessageAdded)
          conversation.on("participantJoined", this.onChatParticipantJoined)
          conversation.on("participantLeft", this.onChatParticipantLeft)
        })
      })
    },
    onDisconnected(room, error) {
      if (error) this.notify(error, "error")
      console.log("Disconnected", room, error)
      this.leave()
    },
    onParticipantConnected(participant) {
      const msg = "User join your channel"
      this.notify(msg, "info")
      this.addUser(participant)
    },
    onParticipantDisconnected(participant) {
      const msg = "User leave your channel"
      this.notify(msg, "info")
      this.deleteUser(participant)
    },
    onRecordingStart() {
      const msg = "Current room is being recorded"
      this.notify(msg, "info")
    },
    onRecordingStopped() {
      const msg = "Current room is no longer recorded"
      this.notify(msg, "info")
    },
    onTrackSubscribed(track, publication, participant) {
      console.log("Remote track was subscribed to", track, publication, participant)
      this.attachTrack(publication, participant)
    },
    onTrackUnsubscribed(track, publication, participant) {
      console.log("Remote track was unsubscribed from", track, publication, participant)
      this.detachTrack(publication)
    },
    onLocalTrackPublished(publication) {
      console.log("Local track has been published", publication)
      this.attachTrack(publication, this.visioRoom.localParticipant)
    },
    onChatMessageAdded(message) {
      this.chatMessages.push(message)
    },
    onChatParticipantJoined(participant) {
      this.chatUsers.push(participant)
    },
    onChatParticipantLeft(participant) {
      this.chatUsers = this.chatUsers.filter(e => e.sid !== participant.sid)
    },
    // VISIO USER - MANAGMENT
    async addUser(participant, isPinned = false) {
      participant.isPined = isPinned
      this.visioUsers.push(participant)

      // Wait elements be rendered before attach tracks to DOM
      await this.$nextTick()

      // Attatch each tracks to DOM
      participant.tracks.forEach(publication => {
        this.attachTrack(publication, participant)
      })
    },
    deleteUser(participant) {
      // Detach all tracks before removing user from list
      participant.tracks.forEach(this.detachTrack)
      this.visioUsers = this.visioUsers.filter(e => e.sid !== participant.sid)
    },
    attachTrack(publication, participant) {
      const parent = participant.isPined
        ? this.$refs.pinedparticipant
        : this.$refs[`participants-${participant.sid}`]?.[0]
      const element = parent?.$refs[`${publication.kind}-${participant.sid}`]
      console.log("Attach track", publication.track, participant, "on", element)
      if (element) publication.track?.attach(element)
    },
    detachTrack(publication) {
      publication.track?.detach()
    },
    /**
     * Re-attach participant tracks to right DOM elements
     * @param {*} participant
     */
    updateParticipantTracks(participant) {
      participant.tracks.forEach(publication => {
        this.detachTrack(publication)
        this.attachTrack(publication, participant)
      })
    }
  }
}
</script>

<style lang="scss">
$breakpoint-phone: 600px;
$bgcolor-video: rgb(45, 43, 43);
$border: 1px solid black;

@keyframes rotation {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}

.meeting-container {
  border: $border;
  display: flex;
  flex-direction: column;
  height: 90vh;
  width: 100%;

  .meeting-loading {
    animation: rotation 1s infinite ease-in-out;
  }

  .visio-container {
    display: flex;
    flex: 1 1 auto;
    height: calc(100% - 70px);

    .participants-container {
      background-color: $bgcolor-video;
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;

      .participant-pin {
        border: $border;
        height: 85%;

        .participant {
          position: relative;
          width: 100%;
          height: 100%;
        }
      }

      .participant-list {
        display: flex;
        flex-direction: row;
        height: 15%;
        overflow-y: auto;

        .participant {
          border: $border;
          flex: 0 0 auto;
          overflow: hidden;
          padding-bottom: 5px;
          position: relative;
          width: 25%;
        }
      }
    }
  }
}
</style>
